const MIN_VISIBLE = 1;
// Small safety buffer so tabs never render flush against the container edge.
const WIDTH_BUFFER_PX = 8;

const toWidth = element => {
  if (!element) return 0;
  return Math.ceil(element.getBoundingClientRect().width);
};

// Hidden or detached tab bars shouldn't keep scheduling measurements.
const isVisible = element => Boolean(element && element.isConnected && element.getClientRects().length > 0);

// Computed styles come back as strings ("16px", "normal"); anything unparseable counts as 0.
const toPixels = value => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Each tab costs its own width plus one gap, so the row keeps its spacing from whatever
// follows it.
function fitCount(widths, availableWidth, gap) {
  let used = 0;
  let count = 0;

  for (const width of widths) {
    used += width + gap;
    if (used > availableWidth) break;
    count += 1;
  }

  return count;
}

export default {
  ResponsiveTabs: {
    mounted() {
      this.requestAnimationFrameId = null;
      this.scheduleMeasure = this.scheduleMeasure.bind(this);

      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(this.el);

      // Measure straight away so the first frame is already correct, then again after paint
      // in case fonts or late styles changed the widths.
      this.applyOverflow();
      requestAnimationFrame(() => this.scheduleMeasure());
    },

    // Runs synchronously, before the browser paints, so a patch never shows the wrong tabs.
    updated() {
      this.applyOverflow();
    },

    destroyed() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.requestAnimationFrameId) {
        window.cancelAnimationFrame(this.requestAnimationFrameId);
      }
    },

    scheduleMeasure() {
      if (!this.el?.isConnected) return;
      if (this.requestAnimationFrameId) return;

      this.requestAnimationFrameId = window.requestAnimationFrame(() => {
        this.requestAnimationFrameId = null;
        this.applyOverflow();
      });
    },

    // Attributes are written through `this.js()` so LiveView re-applies them after every
    // patch; plain setAttribute would be wiped by the next re-render.
    flag(element, attribute, on) {
      if (!element) return;
      if (on && !element.hasAttribute(attribute)) this.js().setAttribute(element, attribute, '');
      if (!on && element.hasAttribute(attribute)) this.js().removeAttribute(element, attribute);
    },

    applyOverflow() {
      const tabs = Array.from(this.el.querySelectorAll('[data-responsive-tab]'));
      const items = Array.from(this.el.querySelectorAll('[data-responsive-tabs-item]'));
      const more = this.el.querySelector('[data-responsive-tabs-more]');
      const trailing = this.el.querySelector('[data-responsive-tabs-trailing]');

      if (tabs.length === 0 || !isVisible(this.el)) return;

      // A hidden tab has no width, so everything has to be visible to measure. This only
      // forces layout — no paint happens before the hiding below is applied.
      for (const tab of tabs) this.flag(tab, 'data-overflow', false);
      this.flag(more, 'data-empty', false);

      const containerStyles = window.getComputedStyle(this.el);
      const gap = toPixels(window.getComputedStyle(tabs[0].parentElement).columnGap);
      const containerPadding = toPixels(containerStyles.paddingLeft) + toPixels(containerStyles.paddingRight);

      const containerWidth = this.el.clientWidth;
      // Leave hidden or not-yet-laid-out containers alone until the next observer event.
      if (containerWidth <= 0) return;

      const widths = tabs.map(toWidth);
      // Fonts may still be loading, in which case measuring now would hide everything.
      if (widths.every(width => width <= 0)) return;

      const budget = Math.max(0, containerWidth - containerPadding - toWidth(trailing) - WIDTH_BUFFER_PX);

      let visibleCount = fitCount(widths, budget, gap);

      if (visibleCount < widths.length) {
        visibleCount = fitCount(widths, Math.max(0, budget - toWidth(more) - gap), gap);
      }

      visibleCount = Math.max(MIN_VISIBLE, visibleCount);

      // The selected tab never hides. When it overflows it takes the last slot in the row and
      // the tabs before it give way, so the row always shows what is selected.
      const activeIndex = tabs.findIndex(tab => tab.hasAttribute('data-active'));
      const pinnedIndex = activeIndex >= visibleCount ? activeIndex : -1;

      if (pinnedIndex >= 0) {
        const leading = budget - toWidth(more) - widths[pinnedIndex] - gap * 2;
        visibleCount = fitCount(widths.slice(0, pinnedIndex), Math.max(0, leading), gap);
      }

      const isVisibleTab = index => index < visibleCount || index === pinnedIndex;
      // Pinning means the row can start on a tab that is not the first in the DOM, so `:first-child`
      // cannot style the leading edge — the hook marks it instead.
      const firstVisible = tabs.findIndex((_tab, index) => isVisibleTab(index));

      // Tabs and dropdown entries come from the same list in the same order, so one index
      // decides both: the tab hides when it overflows, its entry when it doesn't.
      tabs.forEach((tab, index) => {
        this.flag(tab, 'data-overflow', !isVisibleTab(index));
        this.flag(tab, 'data-first-visible', index === firstVisible);
      });
      items.forEach((item, index) => this.flag(item, 'hidden', isVisibleTab(index)));

      this.flag(
        more,
        'data-empty',
        tabs.every((_tab, index) => isVisibleTab(index)),
      );
    },
  },
};
